<script setup lang="ts">
import { computed } from 'vue'

import { getParticipantTeamSide } from '@collector/shared-ui-domain'
import { Participant, Stat } from '@collector/sportsapi-client-legacy'
import SportStatIcon from '@desktop/views/Relation/components/RelationView/SportStatIcon.vue'
import { useParticipantStatsMaps } from '@desktop/views/Relation/composables'

import { formatStatValue } from '../../../utils'
import { injectImportantStatsId } from '../../provide-inject'

interface Props {
  participant: Participant
}

const props = defineProps<Props>()

const { homeParticipantStatsMap, awayParticipantStatsMap } =
  useParticipantStatsMaps()

const teamSide = getParticipantTeamSide(props.participant)
const participantStatsMap =
  teamSide === 'home' ? homeParticipantStatsMap : awayParticipantStatsMap
const direction = teamSide === 'home' ? 'left' : 'right'

const importantStatsId = injectImportantStatsId()

const importantStats = computed(() => {
  const stats: Stat[] = []

  if (importantStatsId) {
    for (const statId of importantStatsId) {
      const stat = participantStatsMap.value[statId]
      if (stat) {
        stats.push(stat)
      }
    }
  }

  return stats
})
</script>

<template>
  <div
    class="flex justify-end gap-2"
    :class="
      direction === 'left' ? 'flex-row' : 'flex-row-reverse space-x-reverse'
    "
  >
    <div
      v-for="stat in importantStats"
      :key="stat.id"
      class="flex items-center space-x-0.5"
      :class="{
        'flex-row pl-2': direction === 'left',
        'flex-row-reverse space-x-reverse pr-2': direction === 'right',
      }"
      :data-testid="`stat-${stat.id}-${teamSide}`"
    >
      <SportStatIcon
        :statId="stat.id"
        size="3xs"
      />
      <div class="min-w-3">
        {{ formatStatValue(stat.value) }}
      </div>
    </div>
  </div>
</template>
